import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Activity,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ShieldCheck,
  ShieldAlert,
  Globe,
} from "lucide-react";

const API =
  (typeof window !== "undefined" && window.location
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Pill = ({ ok, warn, label }) => {
  const cls = ok
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : warn
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-rose-50 text-rose-700 border-rose-200";
  const Icon = ok ? CheckCircle2 : warn ? AlertTriangle : XCircle;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cls}`}
    >
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

/**
 * iter323c — Domain Health widget for the Super-Admin Tenant Detail page.
 * Shows per-host DNS / SSL / reachability / CORS status. If any host has
 * "cors_drift" (DNS resolves but not in CORS allow-list), a one-click
 * "Re-add to CORS" button appears.
 */
export default function DomainHealthCard({ tenantId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [refreshingCors, setRefreshingCors] = useState(false);

  const fetchHealth = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/super-admin/tenants/${tenantId}/domain-health`,
        { headers: authHeader() },
      );
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch domain health");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  const refreshCors = async () => {
    setRefreshingCors(true);
    try {
      const res = await axios.post(
        `${API}/super-admin/tenants/${tenantId}/domain-health/refresh-cors`,
        {},
        { headers: authHeader() },
      );
      const added = res.data?.added_count ?? 0;
      toast.success(
        added > 0
          ? `Added ${added} origin${added === 1 ? "" : "s"} to CORS`
          : "CORS list already up to date",
      );
      await fetchHealth();
    } catch (e) {
      toast.error(e.response?.data?.detail || "CORS refresh failed");
    } finally {
      setRefreshingCors(false);
    }
  };

  const hosts = data?.hosts || [];
  const summary = data?.summary || {};
  const showCorsFix = summary.cors_drift;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
      data-testid="domain-health-card"
    >
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-slate-500" />
          <h2 className="text-sm font-semibold tracking-tight text-slate-800">
            Domain Health
          </h2>
          {loading && (
            <Loader2
              className="w-3.5 h-3.5 animate-spin text-slate-400"
              data-testid="domain-health-loading"
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchHealth}
            disabled={loading}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-50"
            data-testid="domain-health-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            Re-check
          </button>
          {showCorsFix && (
            (RETIRED_TOOLS_VISIBLE ? (<button
              onClick={refreshCors}
              disabled={refreshingCors}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid="domain-health-refresh-cors"
              title="Reload the CORS allow-list from tenant_registry (hot; no restart needed)"
            >
              {refreshingCors ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <ShieldCheck className="w-3 h-3" />
              )}
              Re-add to CORS
            </button>) : null)
          )}
        </div>
      </header>

      {!summary.any_configured && !loading && (
        <p
          className="text-xs text-slate-500 italic"
          data-testid="domain-health-empty"
        >
          No custom_domain, subdomain_host, or canonical_hostname configured
          on this tenant.
        </p>
      )}

      {summary.any_configured && (
        <div
          className="flex flex-wrap items-center gap-1.5"
          data-testid="domain-health-summary"
        >
          <Pill ok={summary.all_reachable} label="Reachable" />
          <Pill
            ok={summary.all_in_cors}
            warn={!summary.all_in_cors && !summary.cors_drift}
            label={summary.all_in_cors ? "In CORS" : "Missing from CORS"}
          />
          {summary.any_ssl_expiring_soon && (
            <Pill warn label="SSL < 14 days" />
          )}
          <span className="ml-2 text-[11px] text-slate-400">
            {data?.cors_allowlist_size ?? 0} origins in live CORS list
          </span>
        </div>
      )}

      {hosts.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-xs" data-testid="domain-health-table">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Host</th>
                <th className="text-left px-3 py-2 font-medium">DNS</th>
                <th className="text-left px-3 py-2 font-medium">
                  Reachable
                </th>
                <th className="text-left px-3 py-2 font-medium">SSL</th>
                <th className="text-left px-3 py-2 font-medium">CORS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {hosts.map((h) => (
                <React.Fragment key={h.host}>
                <tr data-testid={`domain-row-${h.host}`}>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-center gap-1.5 text-slate-800 font-mono">
                      <Globe className="w-3 h-3 text-slate-400" />
                      {h.host}
                    </div>
                    {h.error && (
                      <div
                        className="text-[10px] text-rose-600 mt-0.5"
                        title={h.error}
                      >
                        {h.error.length > 60
                          ? `${h.error.slice(0, 60)}…`
                          : h.error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {h.dns_ok ? (
                      <div>
                        <CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-600" />
                        <span className="ml-1 text-[10px] text-slate-400 font-mono">
                          {h.dns_resolved_to}
                        </span>
                      </div>
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-rose-500" />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {h.reachable ? (
                      <div>
                        <CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-600" />
                        {typeof h.http_status === "number" && (
                          <span className="ml-1 text-[10px] text-slate-400">
                            {h.http_status}
                          </span>
                        )}
                      </div>
                    ) : (
                      <XCircle className="w-3.5 h-3.5 text-rose-500" />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {h.ssl_valid ? (
                      <div className="flex items-center gap-1">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                        {typeof h.ssl_days_remaining === "number" && (
                          <span
                            className={`text-[10px] ${
                              h.ssl_days_remaining < 14
                                ? "text-amber-600"
                                : "text-slate-400"
                            }`}
                          >
                            {h.ssl_days_remaining}d
                          </span>
                        )}
                      </div>
                    ) : (
                      <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    {h.in_cors_allowlist ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <span className="inline-flex items-center gap-1 text-rose-600">
                        <XCircle className="w-3.5 h-3.5" />
                        <span className="text-[10px]">missing</span>
                      </span>
                    )}
                  </td>
                </tr>
                {/* iter222 — Backend-computed hint row: shows the
                    human-readable next-action classified by
                    `_probe_host_domain_health.hint_code`. Only rendered
                    for non-OK states to keep the "all green" case quiet. */}
                {h.hint && h.hint_code && h.hint_code !== "ok" && (
                  <tr
                    className="bg-amber-50/40"
                    data-testid={`domain-row-hint-${h.host}`}
                  >
                    <td colSpan={5} className="px-3 py-2">
                      <div className="text-[11px] text-amber-900 flex items-start gap-1.5">
                        <span
                          className="inline-block rounded bg-amber-200/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide shrink-0"
                          title={`hint_code=${h.hint_code}`}
                        >
                          {h.hint_code.replace(/_/g, " ")}
                        </span>
                        <span>{h.hint}</span>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.checked_at && (
        <p className="text-[10px] text-slate-400">
          Checked at{" "}
          <span className="font-mono">{data.checked_at}</span>
        </p>
      )}
    </section>
  );
}
